'use client';

import { VideoHTMLAttributes, useEffect, useRef, useState } from 'react';

import { Styles, css } from '@flipster/styled-system/css';

import { useIntersection } from '../misc/browser/intersection';
import { BreakpointConditions, mediaQueryMapper } from '../misc/browser/media';
import { Picture } from './picture';

type VideoMedia = { base: string } & { [K in Exclude<BreakpointConditions, 'base'>]?: string };

interface Props<T extends VideoMedia = VideoMedia> extends Omit<
  VideoHTMLAttributes<unknown>,
  'src' | 'poster' | 'className'
> {
  src: T;
  poster: T;
  css?: Styles;
  lazy?: boolean;
}

export const Video = <T extends VideoMedia = VideoMedia>({
  src,
  poster,
  lazy,
  css: cssProps,
  ...videoProps
}: Props<T>) => {
  const pictureRef = useRef<HTMLImageElement | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [loaded, setLoaded] = useState<boolean>(false);
  // poster 가 없으면 이미지에서 비디오로 바뀔때 깜빡임이 발생함
  // poster 과 Picture 를 같이 쓴 이유는 서버렌더링 된 결과랑 클라에서 media 를 계산해서 넣는 사이에 하이드레이션 에러 발생
  const [currentPoster, setCurrentPoster] = useState<string | undefined>(undefined);

  useEffect(() => {
    const videoTag = videoRef.current;
    if (!videoTag) return;

    const loadeddataHandler = () => {
      videoTag.play();
    };

    const playingHandler = () => {
      setLoaded(true);
    };

    videoTag.addEventListener('loadeddata', loadeddataHandler);
    videoTag.addEventListener('playing', playingHandler);

    return () => {
      videoTag.removeEventListener('loadeddata', loadeddataHandler);
      videoTag.removeEventListener('playing', playingHandler);
    };
  }, []);

  useEffect(() => {
    const getCurrentPoster = () => {
      const posterSources = Object.entries(poster).map(([condition, src]) => ({
        media: mediaQueryMapper[condition as Exclude<BreakpointConditions, 'base'>],
        src,
      }));
      for (let i = posterSources.length - 1; i >= 0; i--) {
        const { media, src } = posterSources[i];
        if (media && window.matchMedia(media).matches) {
          return src;
        }
      }
      return poster.base;
    };
    setCurrentPoster(getCurrentPoster());
  }, [poster]);

  // lazy load 가 아닐때
  useEffect(() => {
    if (!videoRef.current) return;
    if (!lazy) videoRef.current.load();
  }, [lazy]);
  // lazy load 일때
  useIntersection(pictureRef, ([entry]) => {
    if (!videoRef.current || !lazy) return;
    if (entry.intersectionRatio > 0 && !loaded) {
      videoRef.current.load();
    }
  });

  const videoSources = Object.entries(src)
    .map(([condition, src]) => ({
      media: mediaQueryMapper[condition as Exclude<BreakpointConditions, 'base'>],
      src,
    }))
    .filter(({ media, src }) => media && src);

  const isShowVideo = loaded && !!currentPoster;

  return (
    <>
      <Picture
        src={poster}
        alt='video-poster'
        css={css.raw(cssProps, isShowVideo && { opacity: 0, display: 'none', pointerEvents: 'none' })}
        fetchPriority={lazy ? 'low' : 'high'}
        ref={pictureRef}
      />
      <video
        ref={videoRef}
        poster={currentPoster}
        autoPlay={false}
        muted
        playsInline
        loop
        preload='none'
        className={css(cssProps, !isShowVideo && { opacity: 0, visibility: 'hidden', pointerEvents: 'none', h: 0 })}
        {...videoProps}
      >
        {videoSources.map(({ media, src }) => (
          <source key={src} media={media} src={src} type='video/mp4' />
        ))}
        <source src={src.base} type='video/mp4' />
      </video>
    </>
  );
};
